import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
// @ts-ignore Ignore unused commented imports
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import * as settingActions from '../../../settings/store/actions/settings.actions';
import * as settingSelectors from '../../../settings/store/selectors/settings.selectors';
import { Product } from '../../../shared/models/shared.model';
import { PigContextKeys } from '../../../shared/models/shared.model';
import { ProductModuleState } from '../../models/product.model';
import { UpsertProductResponse } from '../../services/product-service.model';
import { ProductService } from '../../services/product.service';
// @ts-ignore Ignore unused commented ActionTypes import
import * as actions from '../actions/product.actions';
import { getSelectedProductCountry } from '../selectors/product.selectors';

@Injectable()
export class ProductEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly ngZone: NgZone,
    private readonly router: Router,
    private readonly store: Store<ProductModuleState>,
    private readonly localeService: LocaleService,
    private readonly productService: ProductService,
    private readonly dialogService: DialogService,
    private readonly settingsService: SettingsServiceHelper,
    @Inject(WindowServiceInjector) private window: WindowService,
  ) {}

  selectedCountry$: Observable<string | undefined> = combineLatest([
    this.store.select(UserSelectors.selectCustomer),
    this.store.select(getSelectedProductCountry),
  ]).pipe(
    map(([customer, selectedCountry]) => {
      const isCustomerProducts = this.window.getPath().indexOf('/customer-product') !== -1;

      return isCustomerProducts ? customer?.country : selectedCountry;
    }),
    skipWhile((selectedCountry) => selectedCountry === undefined),
  );

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadData),
      switchMap(() => {
        const isCustomerProducts = this.window.getPath().indexOf('/customer-product') !== -1;

        if (isCustomerProducts) {
          return of(
            actions.clearProductState(),
            actions.loadSelectedCountry(),
            settingActions.loadEnergySetting(),
            settingActions.loadLysineSetting(),
          );
        }

        return this.store.select(UserSelectors.selectLocation).pipe(
          skipWhile((location) => !location),
          take(1),
          switchMap((location) =>
            of(
              actions.clearProductState(),
              actions.setSelectedCountry({ save: false, id: location.country }),
              settingActions.loadEnergySetting(),
              settingActions.loadLysineSetting(),
            ),
          ),
        );
      }),
    ),
  );

  getProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getProductsRequest),
      switchMap(({ country }) =>
        combineLatest([
          country ? of(country) : this.selectedCountry$,
          this.store.select(settingSelectors.getEnergySetting),
          this.store.select(settingSelectors.getLysineSetting),
        ]).pipe(
          skipWhile(
            ([selectedCountry, energy, lysine]) =>
              selectedCountry === null ||
              selectedCountry === undefined ||
              energy === null ||
              lysine === null,
          ),
          take(1),
        ),
      ),
      exhaustMap(([selectedCountry, energy, lysine]) =>
        this.productService.getProducts(selectedCountry, energy, lysine).pipe(
          switchMap((products: Product[]) => of(actions.getProductsSuccess({ products }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.getProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getCustomerProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getCustomerProductsRequest),
      switchMap((_) =>
        combineLatest([
          this.store.select(UserSelectors.selectCustomer),
          this.store.select(settingSelectors.getEnergySetting),
          this.store.select(settingSelectors.getLysineSetting),
        ]).pipe(
          skipWhile(
            ([customer, energy, lysine]) => customer === null || energy === null || lysine === null,
          ),
          take(1),
        ),
      ),
      exhaustMap(([customer, energy, lysine]) =>
        this.productService.getProductsByCustomerId(energy, lysine, customer?.id).pipe(
          switchMap((customerProducts: Product[]) =>
            of(actions.getCustomerProductsSuccess({ customerProducts })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.getProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setSelectedProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedProductRequest),
      withLatestFrom(
        this.store.select(settingSelectors.getEnergySetting),
        this.store.select(settingSelectors.getLysineSetting),
      ),
      skipWhile(([_, energy, lysine]) => energy === null || lysine === null),
      exhaustMap(([{ id }, energy, lysine]) =>
        this.productService.getProductById(id, energy, lysine).pipe(
          switchMap((product: Product) => of(actions.setSelectedProductSuccess({ product }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.setSelectedProductError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  upsertProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertProductRequest),
      mergeMap((request) =>
        this.productService.upsertProduct(request.request).pipe(
          switchMap((response: UpsertProductResponse) =>
            of(
              !request.request.customerId
                ? actions.upsertProductSuccess({ response })
                : actions.upsertCustomerProductSuccess({ response }),
              !request.request.customerId
                ? actions.getProductsRequest({})
                : actions.getCustomerProductsRequest(),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('product-saved'),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.upsertProductError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteProductWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('product-overview.delete-confirmation-title'),
            text: this.localeService.translate('product-overview.delete-confirmation-message'),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  actions.deleteProductRequest({
                    product: action.product,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteProductRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductRequest),
      mergeMap((action) =>
        this.productService.deleteProduct(action.product.id).pipe(
          map(() =>
            !Object.prototype.hasOwnProperty.call(action.product, 'customerId') ||
            !action.product.customerId
              ? actions.deleteProductSuccess({ productId: action.product.id })
              : actions.deleteCustomerProductSuccess({ productId: action.product.id }),
          ),
          catchError((_: HttpErrorResponse) =>
            of(
              NotificationActions.errorSnackbar({
                message: this.localeService.translate('product-overview.delete-error-message'),
              }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteProductSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteProductSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/product'])).then();
        }),
      ),
    { dispatch: false },
  );
  deleteCustomerProductSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteCustomerProductSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/customer-product'])).then();
        }),
      ),
    { dispatch: false },
  );

  loadSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadSelectedCountry),
      exhaustMap((_: unknown) =>
        this.settingsService
          .getSingle<string>(
            'NL',
            PigContextKeys.SELECTED_PRODUCT_COUNTRY,
            SettingsStorage.Global,
            true,
          )
          .pipe(
            switchMap((settings: string) =>
              of(actions.setSelectedCountry({ save: false, id: settings })),
            ),
            catchError((error: HttpErrorResponse) =>
              of(NotificationActions.errorSnackbar({ message: error.message })),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedCountry),
      switchMap((action) => {
        if (action.save) {
          this.settingsService.setSettings(
            PigContextKeys.SELECTED_PRODUCT_COUNTRY,
            action.id,
            SettingsStorage.Global,
            true,
          );
        }
        return of(
          actions.setSelectedCountrySuccess({ id: action.id }),
          actions.getProductsRequest({ country: action.id }),
        );
      }),
    ),
  );
}
